import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/App";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import {
  ShoppingCart,
  Heart,
  ChevronLeft,
  Check,
  Truck,
  RotateCcw,
  Shield,
  Star,
} from "lucide-react";

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedColor, setSelectedColor] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [bodyScan, setBodyScan] = useState(null);

  useEffect(() => {
    fetchData();
  }, [productId]);

  const fetchData = async () => {
    try {
      const [productRes, scanRes] = await Promise.all([
        apiClient.get(`/products/${productId}`),
        apiClient.get("/scan").catch(() => ({ data: null })),
      ]);
      setProduct(productRes.data);
      setBodyScan(scanRes.data);
      
      // Set default selections
      if (productRes.data.sizes?.length > 0) {
        // If user has scan data, try to match recommended size
        if (scanRes.data) {
          const recSize = productRes.data.category === "wear" 
            ? (productRes.data.subcategory === "joggers" || productRes.data.subcategory === "footwear"
                ? scanRes.data.recommended_bottom_size
                : scanRes.data.recommended_top_size)
            : productRes.data.sizes[0];
          
          if (productRes.data.sizes.includes(recSize)) {
            setSelectedSize(recSize);
          } else {
            setSelectedSize(productRes.data.sizes[0]);
          }
        } else {
          setSelectedSize(productRes.data.sizes[0]);
        }
      }
      if (productRes.data.colors?.length > 0) {
        setSelectedColor(productRes.data.colors[0]);
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to load product");
      navigate("/wear");
    } finally {
      setLoading(false);
    }
  };

  const addToCart = async () => {
    try {
      await apiClient.post("/cart/add", {
        product_id: product.product_id,
        quantity,
        size: selectedSize,
        color: selectedColor,
      });
      toast.success(`${product.name} added to cart!`);
    } catch (error) {
      toast.error("Failed to add to cart");
    }
  };

  const getRecommendedSize = () => {
    if (!bodyScan || !product) return null;
    if (product.subcategory === "footwear") {
      return bodyScan.recommended_shoe_size;
    } else if (["joggers", "shorts"].includes(product.subcategory)) {
      return bodyScan.recommended_bottom_size;
    }
    return bodyScan.recommended_top_size;
  };

  const recommendedSize = getRecommendedSize();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="text-center py-16">
          <p className="text-muted-foreground">Product not found</p>
          <Button onClick={() => navigate("/wear")} className="mt-4">
            Back to Shop
          </Button>
        </div>
      </Layout>
    );
  }

  const colorMap = {
    black: "#000",
    white: "#fff",
    orange: "#f97316",
    grey: "#6b7280",
    gray: "#6b7280",
    navy: "#1e3a5f",
    green: "#10b981",
    gold: "#fbbf24",
    "black/orange": "#f97316",
    chocolate: "#7b3f00",
    vanilla: "#f3e5ab",
    peanut: "#c19a6b",
    citrus: "#ffd700",
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="product-detail-page">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="aspect-square rounded-3xl overflow-hidden bg-muted">
              <img
                src={product.image_url}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <Badge variant="outline" className="mb-2 capitalize">
                {product.subcategory}
              </Badge>
              <h1 className="font-heading font-bold text-3xl md:text-4xl mb-2">
                {product.name}
              </h1>
              <p className="text-3xl font-heading font-bold text-primary">
                ${product.price.toFixed(2)}
              </p>
            </div>

            <p className="text-muted-foreground">{product.description}</p>

            {/* Size Selection */}
            {product.sizes?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">Size</span>
                  {recommendedSize && (
                    <Badge className="bg-secondary/10 text-secondary border-secondary/20">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      Your size: {recommendedSize}
                    </Badge>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((size) => (
                    <button
                      key={size}
                      onClick={() => setSelectedSize(size)}
                      className={`size-option ${
                        selectedSize === size ? "selected" : ""
                      } ${recommendedSize === size ? "recommended" : ""}`}
                      data-testid={`size-${size}`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Color Selection */}
            {product.colors?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">Color</span>
                  <span className="text-sm text-muted-foreground capitalize">
                    {selectedColor}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(color)}
                      className={`w-10 h-10 rounded-full border-2 transition-all ${
                        selectedColor === color
                          ? "border-primary scale-110"
                          : "border-transparent hover:border-muted-foreground"
                      }`}
                      style={{
                        backgroundColor: colorMap[color.toLowerCase()] || "#d1d5db",
                      }}
                      title={color}
                      data-testid={`color-${color}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            <div>
              <span className="font-medium block mb-3">Quantity</span>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-muted"
                  data-testid="qty-minus"
                >
                  -
                </button>
                <span className="font-medium w-8 text-center">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="w-10 h-10 rounded-full border border-border flex items-center justify-center hover:bg-muted"
                  data-testid="qty-plus"
                >
                  +
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <Button
                size="lg"
                className="flex-1 rounded-full py-6"
                onClick={addToCart}
                disabled={!product.in_stock}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {product.in_stock ? "Add to Cart" : "Out of Stock"}
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full py-6"
                onClick={() => toast.success("Added to wishlist")}
                data-testid="wishlist-btn"
              >
                <Heart className="w-5 h-5" />
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
              {[
                { icon: Truck, label: "Free Shipping", sublabel: "On orders $50+" },
                { icon: RotateCcw, label: "Easy Returns", sublabel: "30-day policy" },
                { icon: Shield, label: "Secure Pay", sublabel: "100% protected" },
              ].map((badge, index) => (
                <div key={index} className="text-center">
                  <badge.icon className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                  <div className="text-sm font-medium">{badge.label}</div>
                  <div className="text-xs text-muted-foreground">{badge.sublabel}</div>
                </div>
              ))}
            </div>

            {/* Points Earning */}
            <div className="p-4 bg-accent/10 rounded-xl flex items-center gap-3">
              <Star className="w-6 h-6 text-accent" />
              <div>
                <span className="font-medium">Earn {Math.round(product.price * 5)} BE Points</span>
                <span className="text-sm text-muted-foreground ml-2">with this purchase</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetail;
